// pattern: Functional Core

/**
 * Flashcard Model - Type definitions and pure functions for flashcard data
 * Functional Core - no side effects
 */

import type { StoredQuestion } from './question-model.ts';

/** Flashcard as persisted in D1 (within StoredQuiz.flashcards) */
export type StoredFlashcard = {
  readonly id: string;
  readonly front: string;
  readonly back: string;
  readonly topic?: string;
  readonly source: 'extracted' | 'generated' | 'manual';
  readonly figureDataUrl?: string;
};

/** Flashcard as returned by AI extraction (before becoming StoredFlashcard) */
export type ExtractedFlashcard = {
  readonly front: string;
  readonly back: string;
  readonly topic: string;
  readonly source: 'extracted' | 'generated';
  readonly figurePageNumber?: number;
  readonly figureDataUrl?: string;
};

/** Result from AI flashcard extraction */
export type FlashcardExtractionResult = {
  readonly documentType: 'quiz_or_test' | 'educational_content' | 'mixed' | 'other';
  readonly summary: string;
  readonly flashcards: ReadonlyArray<ExtractedFlashcard>;
};

/** Generate a random flashcard ID (matches generateId() pattern in editor-view.ts) */
export function generateFlashcardId(): string {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const values = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(values, (v) => chars[v % chars.length]).join('');
}

/** Convert ExtractedFlashcard to StoredFlashcard by assigning an ID */
export function toStoredFlashcard(extracted: ExtractedFlashcard, id: string): StoredFlashcard {
  return {
    id,
    front: extracted.front,
    back: extracted.back,
    topic: extracted.topic,
    source: extracted.source,
    ...(extracted.figureDataUrl ? { figureDataUrl: extracted.figureDataUrl } : {}),
  };
}

/** Convert an MCQ question to a flashcard (question -> front, correct answer -> back) */
export function mcqToFlashcard(question: StoredQuestion): StoredFlashcard {
  return {
    id: question.id,
    front: question.question,
    back: question.options[question.correctAnswerIndex],
    topic: question.topic,
    source: question.source,
    ...(question.figureDataUrl ? { figureDataUrl: question.figureDataUrl } : {}),
  };
}

/** Type guard: is value a valid StoredFlashcard? */
export function isValidFlashcard(value: unknown): value is StoredFlashcard {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj['id'] === 'string' &&
    typeof obj['front'] === 'string' &&
    typeof obj['back'] === 'string' &&
    (obj['source'] === 'extracted' || obj['source'] === 'generated' || obj['source'] === 'manual')
  );
}
